import { useCombobox } from 'downshift';
import React, {
  Children,
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AutocompleteItemOption } from './types';
import clsx from 'clsx/lite';
import AutocompleteItem from './autocompleteItem';

type AutocompleteProps = PropsWithChildren<
  {
    options: AutocompleteItemOption[];
    inputValueRef: string;
    setInputValueRef: (input: string, coordinates?: number[]) => void;
    id: string;
    onOptionClick?: (option: AutocompleteItemOption) => void;
    shouldOpenMenu?: boolean;
    setShouldOpenMenu?: (val: boolean) => void;
    variant?: 'header' | 'page' | 'widget' | 'form';
    enable?: boolean;
    fallbackComponent?: ReactNode;
    onOpenChange?: (isOpen: boolean) => void;
    onSubmit?: () => void;
  } & (
    | { shouldOpenMenu: boolean; setShouldOpenMenu: (val: boolean) => void }
    | { shouldOpenMenu?: undefined; setShouldOpenMenu?: undefined }
  )
>;

const Autocomplete = ({
  children,
  options,
  inputValueRef,
  setInputValueRef,
  shouldOpenMenu,
  setShouldOpenMenu,
  onOptionClick,
  id,
  variant = 'widget',
  enable = true,
  fallbackComponent,
  onOpenChange,
  onSubmit,
}: AutocompleteProps) => {
  const [items, handleItems] = useState(options);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ placement: 'top' | 'bottom'; maxHeight: number }>({
    placement: 'bottom',
    maxHeight: 384,
  });

  const updateInputValueRef = (newValue: string, extra?: { coordinates?: number[] }) =>
    extra?.coordinates ? setInputValueRef(newValue, extra.coordinates) : setInputValueRef(newValue);

  const combobox = useCombobox({
    onInputValueChange: ({ inputValue = '' }) => {
      updateInputValueRef(inputValue, { coordinates: options.find(opt => opt.value === inputValue)?.coordinates });
      handleItems(options);
    },
    items: items.map(item => item.value),
    inputValue: inputValueRef,
    onSelectedItemChange: changes => {
      const selectedOption = options.find(opt => opt.value === changes.inputValue);
      if (!enable || typeof changes.inputValue !== 'string' || !selectedOption) return;
      updateInputValueRef(changes.inputValue, { coordinates: selectedOption.coordinates });
      onOptionClick?.(selectedOption);
    },
    id: id,
    inputId: `${id}-input`,
    labelId: `${id}-label`,
    menuId: `${id}-items-list`,
    getItemId: index => `${id}-item-${index}`,
    initialHighlightedIndex: !enable || !inputValueRef ? -1 : 0,
  });

  const isMenuVisible = combobox.isOpen && (!!items.length || !!fallbackComponent);

  const updateMenuPosition = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof window === 'undefined') return;

    const gap = 8;
    const viewportPadding = 16;
    const preferredMaxHeight = 384;
    const minimumUsefulSpace = preferredMaxHeight / 2;
    const rect = wrapper.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
    const spaceAbove = rect.top - gap - viewportPadding;
    const shouldOpenUp = spaceBelow < minimumUsefulSpace && spaceAbove > spaceBelow;
    const availableSpace = Math.max(96, shouldOpenUp ? spaceAbove : spaceBelow);

    setMenuPosition({
      placement: shouldOpenUp ? 'top' : 'bottom',
      maxHeight: Math.min(preferredMaxHeight, availableSpace),
    });
  }, []);

  useEffect(() => {
    if (shouldOpenMenu) {
      combobox.openMenu();
      setShouldOpenMenu(false);
    }
  }, [combobox, shouldOpenMenu, setShouldOpenMenu]);

  useEffect(() => {
    if (onOpenChange) onOpenChange(combobox.isOpen);
  }, [combobox.isOpen, onOpenChange]);

  useEffect(() => {
    if (!isMenuVisible) return;

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isMenuVisible, items.length, updateMenuPosition]);

  useEffect(() => {
    handleItems(options);
    combobox.setHighlightedIndex(!enable || !inputValueRef ? -1 : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combobox.inputValue, inputValueRef, options]);

  if (!children) return null;

  const menuStyle: CSSProperties = {
    maxHeight: menuPosition.maxHeight,
  };

  return (
    <div ref={wrapperRef} className="group relative h-full w-full" data-cy="autocomplete">
      {Children.map(children, child => {
        if (isValidElement(child)) {
          const props = combobox.getInputProps();
          const childProps = (child as React.ReactElement<any>).props;
          const refPropName = 'innerRef' in childProps ? 'innerRef' : 'ref';
          return cloneElement(child as React.ReactElement<any>, {
            ...props,
            [refPropName]: (props as any).ref,
            onClick: (e: React.MouseEvent) => {
              props?.onClick?.(e);
              childProps?.onClick?.(e);
            },
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' && onSubmit) {
                e.preventDefault();
                combobox.closeMenu();
                onSubmit();
                return;
              }
              props?.onKeyDown?.(e);
            },
            'aria-labelledby': '',
          });
        }
        return child;
      })}
      <ul
        className={clsx(
          'absolute z-50 w-full overflow-y-auto overflow-x-hidden rounded-md bg-neutral-surface text-slate-500 shadow-xl',
          menuPosition.placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
          variant === 'page' && 'md:w-[20rem] lg:w-[34rem]',
          variant === 'header' && 'md:w-[375px] lg:w-[460px] xl:w-[528px]',
          variant === 'form' && 'w-[24rem] md:w-[34rem]',
          combobox.isOpen && !!items.length && 'z-[1050] py-3',
        )}
        style={menuStyle}
        {...combobox.getMenuProps({ 'aria-label': 'Scegli un opzione' })}
      >
        {fallbackComponent ??
          (combobox.isOpen &&
            items.map((item, index) => (
              <AutocompleteItem
                key={`${item.value}-${index}`}
                item={item}
                index={index}
                combobox={combobox}
                isHighlighted={combobox.highlightedIndex === index}
              />
            )))}
      </ul>
    </div>
  );
};

export default Autocomplete;
